import React, { useState, useEffect } from "react";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";
import { toast } from "react-hot-toast";
import UserEditModal from "./UserEditModal";

const UsersTab = () => {
    const Auth = useAuthentication();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
    const [filterName, setFilterName] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterTeam, setFilterTeam] = useState('');

    const loadUsers = () => {
        setLoading(true);
        axios({
            method: "get",
            auth: Auth.getAuthObject(),
            url: SyncConstants["syncBaseURL"] + "users",
            responseType: 'json'
        }).then((response) => {
            setUsers(response.data);
            setLoading(false);
        }).catch((error) => {
            toast.error("Fehler beim Laden der Benutzer");
            setLoading(false);
        });
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setShowEditModal(true);
    };

    const handleCreateUser = () => {
        setSelectedUser(null);
        setShowCreateModal(true);
    };

    const handleDeleteUser = (user) => {
        if (!confirm(`Möchtest du den Benutzer "${user.real_name}" (${user.username}) wirklich löschen?`)) {
            return;
        }

        axios({
            method: "delete",
            auth: Auth.getAuthObject(),
            url: SyncConstants["syncBaseURL"] + "user/delete",
            params: { id: user.id }
        }).then(() => {
            toast.success("Benutzer gelöscht");
            loadUsers();
        }).catch((error) => {
            const errorMsg = error.response?.data?.error || "Fehler beim Löschen";
            toast.error(errorMsg);
        });
    };

    const handleModalClose = (reload = false) => {
        setShowEditModal(false);
        setShowCreateModal(false);
        setSelectedUser(null);
        if (reload) {
            loadUsers();
        }
    };

    const getRoleBadge = (role) => {
        return role === 'administrator'
            ? <span className="badge bg-danger">Administrator</span>
            : <span className="badge bg-secondary">Benutzer</span>;
    };

    const getStatusBadge = (status) => {
        return status === 'active'
            ? <span className="badge bg-success">Aktiv</span>
            : <span className="badge bg-warning">Inaktiv</span>;
    };

    // Filter users
    const filteredUsers = users.filter(user => {
        const nameMatch = !filterName ||
            user.real_name.toLowerCase().includes(filterName.toLowerCase()) ||
            user.username.toLowerCase().includes(filterName.toLowerCase()) ||
            user.email.toLowerCase().includes(filterName.toLowerCase());
        const roleMatch = !filterRole || user.role === filterRole;
        const statusMatch = !filterStatus || user.status === filterStatus;
        const teamMatch = !filterTeam || user.user_group === filterTeam;
        return nameMatch && roleMatch && statusMatch && teamMatch;
    });

    // Get unique teams from users
    const uniqueTeams = [...new Set(users.map(u => u.user_group).filter(Boolean))].sort();

    const resetFilters = () => {
        setFilterName('');
        setFilterRole('');
        setFilterStatus('');
        setFilterTeam('');
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Lädt...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>
                    <i className="bi bi-people me-2 text-primary"></i>
                    Benutzerverwaltung
                </h3>
                <div className="d-flex gap-2">
                    <div className="btn-group btn-group-sm">
                        <button
                            className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewMode('cards')}
                            title="Kartenansicht"
                        >
                            <i className="bi bi-grid-3x3-gap"></i>
                        </button>
                        <button
                            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewMode('list')}
                            title="Listenansicht"
                        >
                            <i className="bi bi-list-ul"></i>
                        </button>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={handleCreateUser}>
                        <i className="bi bi-plus-circle me-2"></i>
                        Neuer Benutzer
                    </button>
                </div>
            </div>

            {/* Filter Toolbar */}
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-white border-bottom">
                    <div className="d-flex align-items-center justify-content-between">
                        <h5 className="mb-0">
                            <i className="bi bi-funnel me-2 text-primary"></i>
                            Filter
                        </h5>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={resetFilters}
                            title="Alle Filter zurücksetzen"
                        >
                            <i className="bi bi-x-circle me-1"></i>
                            Zurücksetzen
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        {/* Name/Email Filter */}
                        <div className="col-lg-3 col-md-6">
                            <label className="form-label fw-semibold small text-muted">
                                <i className="bi bi-search me-1"></i>
                                Name / E-Mail / Username
                            </label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="bi bi-person text-muted"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0 ps-2"
                                    placeholder="Suchen..."
                                    value={filterName}
                                    onChange={e => setFilterName(e.target.value)}
                                />
                                {filterName && (
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setFilterName('')}
                                    >
                                        <i className="bi bi-x"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Role Filter */}
                        <div className="col-lg-3 col-md-6">
                            <label className="form-label fw-semibold small text-muted">
                                <i className="bi bi-shield-check me-1"></i>
                                Rolle
                            </label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="bi bi-shield text-muted"></i>
                                </span>
                                <select
                                    value={filterRole}
                                    className="form-select border-start-0 ps-2"
                                    onChange={e => setFilterRole(e.target.value)}
                                >
                                    <option value="">Alle Rollen</option>
                                    <option value="administrator">Administrator</option>
                                    <option value="user">Benutzer</option>
                                </select>
                            </div>
                        </div>

                        {/* Team Filter */}
                        <div className="col-lg-3 col-md-6">
                            <label className="form-label fw-semibold small text-muted">
                                <i className="bi bi-people-fill me-1"></i>
                                Team
                            </label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="bi bi-people text-muted"></i>
                                </span>
                                <select
                                    value={filterTeam}
                                    className="form-select border-start-0 ps-2"
                                    onChange={e => setFilterTeam(e.target.value)}
                                >
                                    <option value="">Alle Teams</option>
                                    {uniqueTeams.map(team => (
                                        <option key={team} value={team}>{team}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div className="col-lg-3 col-md-6">
                            <label className="form-label fw-semibold small text-muted">
                                <i className="bi bi-check-circle me-1"></i>
                                Status
                            </label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="bi bi-toggle-on text-muted"></i>
                                </span>
                                <select
                                    value={filterStatus}
                                    className="form-select border-start-0 ps-2"
                                    onChange={e => setFilterStatus(e.target.value)}
                                >
                                    <option value="">Alle Status</option>
                                    <option value="active">Aktiv</option>
                                    <option value="inactive">Inaktiv</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(filterName || filterRole || filterStatus || filterTeam) && (
                        <div className="mt-3 pt-3 border-top">
                            <small className="text-muted fw-semibold me-2">Aktive Filter:</small>
                            {filterName && (
                                <span className="badge bg-primary me-2">
                                    <i className="bi bi-search me-1"></i>
                                    {filterName}
                                    <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setFilterName('')}></i>
                                </span>
                            )}
                            {filterRole && (
                                <span className="badge bg-info me-2">
                                    <i className="bi bi-shield me-1"></i>
                                    {filterRole === 'administrator' ? 'Administrator' : 'Benutzer'}
                                    <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setFilterRole('')}></i>
                                </span>
                            )}
                            {filterTeam && (
                                <span className="badge bg-warning me-2">
                                    <i className="bi bi-people me-1"></i>
                                    {filterTeam}
                                    <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setFilterTeam('')}></i>
                                </span>
                            )}
                            {filterStatus && (
                                <span className="badge bg-success me-2">
                                    <i className="bi bi-toggle-on me-1"></i>
                                    {filterStatus === 'active' ? 'Aktiv' : 'Inaktiv'}
                                    <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setFilterStatus('')}></i>
                                </span>
                            )}
                            <small className="text-muted">({filteredUsers.length} Ergebnis{filteredUsers.length !== 1 ? 'se' : ''})</small>
                        </div>
                    )}
                </div>
            </div>

            {filteredUsers.length === 0 ? (
                <div className="alert alert-info m-3">
                    <i className="bi bi-info-circle me-2"></i>
                    Keine Benutzer gefunden.
                </div>
            ) : viewMode === 'cards' ? (
                <div className="row g-3">
                    {filteredUsers.map((user) => (
                        <div key={user.id} className="col-md-6 col-lg-4">
                            <div className="card shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div className="flex-grow-1">
                                            <h5 className="card-title mb-1">
                                                <i className="bi bi-person-circle me-2 text-primary"></i>
                                                {user.real_name}
                                            </h5>
                                            <p className="text-muted small mb-0">
                                                <i className="bi bi-at me-1"></i>
                                                {user.username}
                                            </p>
                                        </div>
                                        <div>
                                            {getStatusBadge(user.status)}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="small mb-2">
                                            <i className="bi bi-envelope me-2 text-muted"></i>
                                            {user.email}
                                        </div>
                                        <div className="small mb-2">
                                            <i className="bi bi-shield-check me-2 text-muted"></i>
                                            {getRoleBadge(user.role)}
                                        </div>
                                        <div className="small">
                                            <i className="bi bi-people-fill me-2 text-muted"></i>
                                            {user.user_group || <span className="text-muted">Kein Team</span>}
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2 mt-3">
                                        <button
                                            className="btn btn-sm btn-outline-primary flex-grow-1"
                                            onClick={() => handleEditUser(user)}
                                        >
                                            <i className="bi bi-pencil me-1"></i>
                                            Bearbeiten
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleDeleteUser(user)}
                                            title="Löschen"
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="card shadow-sm">
                    <div className="card-body p-0">
                        <table className="table mb-0 small table-hover">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th className="align-middle border-end">
                                        <i className="bi bi-person me-1"></i>
                                        Name
                                    </th>
                                    <th className="align-middle border-end">
                                        <i className="bi bi-at me-1"></i>
                                        Username
                                    </th>
                                    <th className="align-middle border-end">
                                        <i className="bi bi-envelope me-1"></i>
                                        E-Mail
                                    </th>
                                    <th className="align-middle border-end">
                                        <i className="bi bi-shield me-1"></i>
                                        Rolle
                                    </th>
                                    <th className="align-middle border-end">
                                        <i className="bi bi-people me-1"></i>
                                        Team
                                    </th>
                                    <th className="align-middle border-end">
                                        <i className="bi bi-toggle-on me-1"></i>
                                        Status
                                    </th>
                                    <th className="align-middle text-center" style={{width: '120px'}}>
                                        <i className="bi bi-gear me-1"></i>
                                        Aktionen
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td className="align-middle">
                                            <i className="bi bi-person-circle me-2 text-primary"></i>
                                            <strong>{user.real_name}</strong>
                                        </td>
                                        <td className="align-middle">{user.username}</td>
                                        <td className="align-middle">{user.email}</td>
                                        <td className="align-middle">{getRoleBadge(user.role)}</td>
                                        <td className="align-middle">{user.user_group || <span className="text-muted">-</span>}</td>
                                        <td className="align-middle">{getStatusBadge(user.status)}</td>
                                        <td className="align-middle text-center">
                                            <div className="btn-group btn-group-sm">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleEditUser(user)}
                                                    title="Bearbeiten"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger"
                                                    onClick={() => handleDeleteUser(user)}
                                                    title="Löschen"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showEditModal && (
                <UserEditModal
                    user={selectedUser}
                    onClose={handleModalClose}
                />
            )}

            {showCreateModal && (
                <UserEditModal
                    user={null}
                    onClose={handleModalClose}
                />
            )}
        </>
    );
};

export default UsersTab;
